import { COLOR_RANGE_KIND, type ColorRangeFormatting } from '@ardoq/api-types';
import { getAttributeFormattingValue } from './filterUtils';
import { scaleColor } from '@ardoq/color-helpers';

export const toNumericFieldValue = (rawValue: unknown): number | undefined => {
  if (typeof rawValue === 'number') {
    return Number.isFinite(rawValue) ? rawValue : undefined;
  }

  if (typeof rawValue === 'string') {
    const parsed = parseFloat(rawValue);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

export const calculatePercentInRange = (
  value: number,
  sortedValues: number[]
): number => {
  const min = sortedValues[0];
  const max = sortedValues[sortedValues.length - 1];

  if (min === max) {
    return 0;
  }

  const range = max - min;
  const position = value - min;
  const percent = (position / range) * 100;

  return Math.max(0, Math.min(100, percent));
};

export const calculatePercentileInRange = (
  value: number,
  sortedValues: number[]
): number => {
  if (sortedValues.length === 0) {
    return 0;
  }

  let count = 0;
  for (const v of sortedValues) {
    if (v <= value) {
      count++;
    } else {
      break;
    }
  }
  const percentile = (count / sortedValues.length) * 100;

  return Math.max(1, Math.min(99, percentile));
};

export const resolveColorRangeColor = (
  colorRangeFormatting: ColorRangeFormatting,
  fieldValue: number,
  sortedValues: number[] | null
): string | undefined => {
  if (
    colorRangeFormatting.min.value === null ||
    colorRangeFormatting.max.value === null
  ) {
    return undefined;
  }

  if (colorRangeFormatting.mode === 'number') {
    return scaleColor(colorRangeFormatting, fieldValue);
  }

  if (
    colorRangeFormatting.mode === 'percent' ||
    colorRangeFormatting.mode === 'percentile'
  ) {
    if (!sortedValues) {
      return undefined;
    }

    const normalized =
      colorRangeFormatting.mode === 'percent'
        ? calculatePercentInRange(fieldValue, sortedValues)
        : calculatePercentileInRange(fieldValue, sortedValues);

    if (
      normalized < colorRangeFormatting.min.value ||
      normalized > colorRangeFormatting.max.value
    ) {
      return undefined;
    }

    return scaleColor(colorRangeFormatting, normalized);
  }

  return scaleColor(colorRangeFormatting, fieldValue);
};

export const getColorRangeFormatting = (
  isFormattingFilter: boolean,
  filterValue: unknown,
  filterType: string | undefined
): ColorRangeFormatting | null =>
  getAttributeFormattingValue<ColorRangeFormatting>(
    isFormattingFilter,
    filterValue,
    filterType,
    (value): value is ColorRangeFormatting =>
      (value as { kind?: unknown })?.kind === COLOR_RANGE_KIND
  );
